import moment from 'moment';
import { memo, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Col, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import NoteDropdown from '../noteDropdown';
import { useClickAway } from 'react-use';
import {
  getDragDropStyle,
  isHiddenInPrint,
  itemBottomGapStyle,
  itemTitleStyle,
  itemSettingStyle,
  lineHeightStyle,
} from '../../../../utils/helpers';
import ChiefComplainDeleteBtn from '../chiefComplainDeleteBtn';
import SelectedItemSearch from './selectedItemSearch';

const TreatmentPlan = ({
  element,
  prescriptionItems,
  setIsTreatmentPlanModal,
  deletePrescriptionItem,
  selectedTreatmentPlans,
  setSelectedTreatmentPlans,
  isHistoryPage = false,
  isDelBtn = true,
}) => {
  const userInfo = useSelector((state) => state.authReducer.data);
  const allNotes = useSelector(
    (state) =>
      state?.prescriptionReducer?.prescriptionInfo?.itemElementNotes || [],
  );
  const [isEdit, setIsEdit] = useState('');

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const reorderedItems = reorder(
      selectedTreatmentPlans,
      result.source.index,
      result.destination.index,
    );
    setSelectedTreatmentPlans(reorderedItems);
  };

  // this function to help us with reordering the result
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const handleExtras = (item, fieldName, val) => {
    const objIndex = selectedTreatmentPlans.findIndex(
      (complain) => complain.name === item.name,
    );
    selectedTreatmentPlans[objIndex][fieldName] = val;
    setSelectedTreatmentPlans([...selectedTreatmentPlans]);
  };

  const ref = useRef(null);
  useClickAway(ref, (e) => {
    setIsEdit('');
  });

  const editMode = (item) => {
    return (
      <Row ref={ref} className="selected-row-edit" key={item.name}>
        <Col className="name pl-0 pr-0" lg="4" md="4" sm="12" xs="12">
          <SelectedItemSearch
            apiUrl="treatment-plans"
            selectedItemName={item.name}
            selectedItems={selectedTreatmentPlans}
            selectedItemsAction={setSelectedTreatmentPlans}
          />
        </Col>
        <Col lg="3" md="3" sm="12" xs="12" className="responsive">
          <Form.Control
            type="date"
            size="sm"
            placeholder="Enter duration"
            dvalue={item.date}
            defaultValue={''}
            onChange={(e) => handleExtras(item, 'date', e.target.value)}
          />
        </Col>
        <Col lg="4" md="4" sm="12" xs="12" className="responsive pl-0 pr-0">
          <NoteDropdown
            value={item?.note || ''}
            onChange={(val) => handleExtras(item, 'note', val)}
            placeholder="Add Note"
            suggestions={allNotes.filter(
              (n) =>
                n.elementType === 'treatmentPlan' &&
                n.itemElement?.toLowerCase() === item.name?.toLowerCase(),
            )}
          />
        </Col>
        <Col md="1" className="text-right pr-0">
          <i
            className="far fa-times-circle pt-2"
            aria-hidden="true"
            onClick={() => setIsEdit('')}
          ></i>
        </Col>
      </Row>
    );
  };

  const prescriptionItemCSSObject = () =>
    itemBottomGapStyle(element, selectedTreatmentPlans.length === 0);

  return (
    <div
      className={`row item-row ${
        selectedTreatmentPlans?.length ? 'data-item' : ''
      }`}
      key={`treatment-plan`}
      style={prescriptionItemCSSObject()}
    >
      <span
        className={`prescription-item ${
          isHiddenInPrint(prescriptionItems, 'treatment-plan')
            ? 'item-title-hide'
            : ''
        }`}
        style={itemTitleStyle(
          prescriptionItems?.items?.['treatment-plan']?.titleStyle || {},
        )}
        {...(!isHistoryPage
          ? {
              onClick: (e) => {
                e.stopPropagation();
                setIsTreatmentPlanModal(true);
              },
            }
          : {})}
      >
        {element?.alterName?.length ? (
          <span>{element?.alterName}</span>
        ) : (
          <span>Treatment Plan</span>
        )}
      </span>
      {selectedTreatmentPlans?.length > 0 ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <ul
                className="sub-child-list drag-drop"
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={lineHeightStyle('treatment-plan', prescriptionItems)}
              >
                {selectedTreatmentPlans?.map((item, index) => (
                  <Draggable
                    key={item.name}
                    draggableId={item.name}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <li
                        key={index}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...getDragDropStyle(
                            snapshot.isDragging,
                            provided.draggableProps.style,
                          ),
                          position: 'relative',
                        }}
                      >
                        <div
                          onClick={() => setIsEdit(index)}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <span
                            style={itemSettingStyle(
                              prescriptionItems?.items?.['treatment-plan']
                                ?.itemStyle || {},
                            )}
                          >
                            {item.name}{' '}
                            {item.date && item.date !== ''
                              ? '- ' + moment(item.date).format('DD-MM-YYYY')
                              : ''}
                            {prescriptionItems?.items?.['treatment-plan']
                              ?.notePosition === 'same-line' && item.note
                              ? ` - ${item.note}`
                              : ''}
                          </span>
                          {!isHistoryPage && (
                            <ChiefComplainDeleteBtn
                              action={deletePrescriptionItem}
                              itemName={'treatmentPlan'}
                              delId={item.name}
                            />
                          )}
                        </div>
                        {item.note &&
                        prescriptionItems?.items?.['treatment-plan']
                          ?.notePosition !== 'same-line' ? (
                          <div
                            style={itemSettingStyle(
                              prescriptionItems?.items?.['treatment-plan']
                                ?.subItemStyle || {},
                            )}
                            className="pl-2 font-italic"
                          >
                            - {item.note}
                          </div>
                        ) : (
                          ''
                        )}
                        {index === isEdit && editMode(item)}
                      </li>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        ''
      )}
    </div>
  );
};

export default memo(TreatmentPlan);
